import {
  Order,
  OrderStatusEnum as OrderStatus,
  PaginationMeta,
  OrderFilterOptions,
  Activity,
  ActivityFilterOptions,
} from "@hydrologiq/gql-experience/graphql"
import {
  FillingSiteFilterOptions,
  OrderEvent,
} from "@hydrologiq/gql/graphql"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"

// Stable empty array reference to prevent unnecessary re-renders
const EMPTY_ORDERS_ARRAY: Order[] = []
const EMPTY_ACTIVITIES_ARRAY: Activity[] = []

interface OrdersState {
  value: Order[] | undefined
  pagination: PaginationMeta | undefined
  ordersFilterOptions: OrderFilterOptions | undefined
  loading: boolean
  error: string | null
  activities: Activity[] | undefined
  activitiesPagination: PaginationMeta | undefined
  activitiesLoading: boolean
  activitiesError: string | null
  activitiesFilterOptions: ActivityFilterOptions | undefined
}

const initialState = {
  value: undefined,
  pagination: undefined,
  ordersFilterOptions: undefined,
  loading: false,
  error: null,
  activities: undefined,
  activitiesPagination: undefined,
  activitiesLoading: false,
  activitiesError: null,
  activitiesFilterOptions: undefined,
} satisfies OrdersState as OrdersState

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  selectors: {
    getOrders: (state): Order[] => {
      return state.value ?? EMPTY_ORDERS_ARRAY
    },

    getOrderById: (state, action: string | undefined): Order | undefined => {
      if (!action) return undefined
      const order = state.value?.find((order) => order.id === action)
      return order
    },

    getOrdersPagination: (state): PaginationMeta | undefined => {
      return state.pagination
    },

    getOrdersFilterOptions: (state): FilterOptions | undefined => {
      return state.ordersFilterOptions
    },

    getOrdersLoading: (state): boolean => {
      return state.loading
    },

    getOrdersError: (state): string | null => {
      return state.error
    },

    getActivities: (state): Activity[] => {
      return state.activities ?? EMPTY_ACTIVITIES_ARRAY
    },

    getActivitiesPagination: (state): PaginationMeta | undefined => {
      return state.activitiesPagination
    },

    getActivitiesLoading: (state): boolean => {
      return state.activitiesLoading
    },

    getActivitiesError: (state): string | null => {
      return state.activitiesError
    },
    getActivitiesFilterOptions: (state): FilterOptions | undefined => {
      return state.activitiesFilterOptions
    },
  },
  reducers: {
    setOrders: (state, action: PayloadAction<Order[]>) => {
      state.value = action.payload
    },

    setOrdersWithPagination: (state, action: PayloadAction<{ orders: Order[]; pagination: PaginationMeta }>) => {
      state.value = action.payload.orders
      state.pagination = action.payload.pagination
    },

    setOrdersFilterOptions: (state, action: PayloadAction<OrderFilterOptions | undefined>) => {
      state.ordersFilterOptions = action.payload
    },

    setOrdersLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    setOrdersError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    addOrder: (state, action: PayloadAction<Order>) => {
      state.value = [action.payload, ...(state.value || [])]
      // Update pagination metadata when adding a order
      if (state.pagination) {
        state.pagination.total_count += 1
        // Recalculate total pages based on new count
        state.pagination.total_pages = Math.ceil(state.pagination.total_count / state.pagination.per_page)
        // Update has_next if we now have more pages
        if (state.pagination.page < state.pagination.total_pages) {
          state.pagination.has_next = true
        }
      }
    },
    updateOrder: (state, action: PayloadAction<Order>) => {
      state.value = state.value?.map((order) => (order.id === action.payload.id ? action.payload : order))
    },
    removeOrder: (state, action: PayloadAction<Order>) => {
      state.value = state.value?.filter((order) => order.id !== action.payload.id)
      // Update pagination metadata when removing a order
      if (state.pagination) {
        state.pagination.total_count = Math.max(0, state.pagination.total_count - 1)
        // Recalculate total pages based on new count
        state.pagination.total_pages = Math.ceil(state.pagination.total_count / state.pagination.per_page)
        // Update has_next if we now have fewer pages
        if (state.pagination.page >= state.pagination.total_pages) {
          state.pagination.has_next = false
        }
        // If we're on a page that no longer exists, ensure we don't show it
        if (state.pagination.page > state.pagination.total_pages && state.pagination.total_pages > 0) {
          state.pagination.page = state.pagination.total_pages
          state.pagination.has_prev = state.pagination.page > 1
        }
      }
    },
    updateOrderLatestEvent: (state, action: PayloadAction<OrderEvent>) => {
      state.value = state.value?.map((order) => {
        if (order.id === action.payload.order) {
          return { ...order, latest_event: action.payload }
        }
        return order
      })
    },
    orderComplete: (state, action: PayloadAction<OrderEvent>) => {
      state.value = state.value?.map((order) => {
        if (order.id === action.payload.order) {
          return { ...order, status: OrderStatus.Completed, end: action.payload.created_at }
        }
        return order
      })
    },

    // Activities actions
    setActivities: (state, action: PayloadAction<Activity[]>) => {
      state.activities = action.payload
    },

    setActivitiesWithPagination: (
      state,
      action: PayloadAction<{ activities: Activity[]; pagination: PaginationMeta }>
    ) => {
      state.activities = action.payload.activities
      state.activitiesPagination = action.payload.pagination
    },

    setActivitiesLoading: (state, action: PayloadAction<boolean>) => {
      state.activitiesLoading = action.payload
    },

    setActivitiesError: (state, action: PayloadAction<string | null>) => {
      state.activitiesError = action.payload
    },
    setActivitiesFilterOptions: (state, action: PayloadAction<ActivityFilterOptions | undefined>) => {
      state.activitiesFilterOptions = action.payload
    },
  },
})

export const {
  setOrders,
  setOrdersWithPagination,
  setOrdersFilterOptions,
  setOrdersLoading,
  setOrdersError,
  addOrder,
  updateOrder,
  removeOrder,
  updateOrderLatestEvent,
  orderComplete,
  setActivities,
  setActivitiesWithPagination,
  setActivitiesLoading,
  setActivitiesError,
  setActivitiesFilterOptions,
} = ordersSlice.actions

export const {
  getOrders,
  getOrderById,
  getOrdersPagination,
  getOrdersFilterOptions,
  getOrdersLoading,
  getOrdersError,
  getActivities,
  getActivitiesPagination,
  getActivitiesLoading,
  getActivitiesError,
  getActivitiesFilterOptions,
} = ordersSlice.selectors
export default ordersSlice.reducer
